/* eslint-disable multiline-ternary */
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Spinner,
  Text,
  useToast
} from '@chakra-ui/react'
import { ArrowDownIcon, ArrowUpIcon, CloseIcon } from '@chakra-ui/icons'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { type QueueEntryType } from '../../model/queue/QueueEntry'
import { getQueueService } from '../../services/Services'
import messages from './messages'

interface QueueListProps {
  doctorId: string
}

export default function QueueList({ doctorId }: QueueListProps) {
  const [entries, setEntries] = useState<QueueEntryType[]>([])
  const [loading, setLoading] = useState(true)
  const [actionInProgress, setActionInProgress] = useState<string | null>(null)
  const toast = useToast()
  const intl = useIntl()
  const { formatMessage } = intl

  useEffect(() => {
    const unsubscribe = getQueueService().getTodayQueue(doctorId, (updated) => {
      setEntries(updated)
      setLoading(false)
    })
    return () => {
      unsubscribe()
    }
  }, [doctorId])

  const waitingEntries = entries.filter((e) => e.status === 'waiting')

  const handleMoveUp = async (entry: QueueEntryType) => {
    const idx = waitingEntries.findIndex((e) => e.id === entry.id)
    if (idx <= 0) return
    const above = waitingEntries[idx - 1]
    setActionInProgress(entry.id)
    try {
      await getQueueService().updatePosition(entry, above)
    } catch {
      toast({
        title: formatMessage(messages.failedToReorder),
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    } finally {
      setActionInProgress(null)
    }
  }

  const handleMoveDown = async (entry: QueueEntryType) => {
    const idx = waitingEntries.findIndex((e) => e.id === entry.id)
    if (idx < 0 || idx >= waitingEntries.length - 1) return
    const below = waitingEntries[idx + 1]
    setActionInProgress(entry.id)
    try {
      await getQueueService().updatePosition(entry, below)
    } catch {
      toast({
        title: formatMessage(messages.failedToReorder),
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    } finally {
      setActionInProgress(null)
    }
  }

  const handleRemove = async (entry: QueueEntryType) => {
    setActionInProgress(entry.id)
    try {
      await getQueueService().removeFromQueue(entry.id, doctorId)
      toast({
        title: formatMessage(messages.patientRemovedFromQueue, {
          name: entry.patientName
        }),
        status: 'info',
        duration: 2000,
        isClosable: true
      })
    } catch {
      toast({
        title: formatMessage(messages.failedToRemovePatient),
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    } finally {
      setActionInProgress(null)
    }
  }

  const handleDone = async (entry: QueueEntryType) => {
    setActionInProgress(entry.id)
    try {
      await getQueueService().updateStatus(entry.id, 'done')
    } catch {
      toast({
        title: formatMessage(messages.failedToMarkDone),
        status: 'error',
        duration: 2000
      })
    } finally {
      setActionInProgress(null)
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" py={12}>
        <Spinner size="lg" color="blue.500" />
      </Flex>
    )
  }

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      shadow="sm"
      overflow="hidden"
    >
      <Box px={6} py={4} borderBottom="1px solid" borderColor="gray.100">
        <Text fontSize="lg" fontWeight="semibold" color="gray.700">
          {formatMessage(messages.todaysQueue)}
          {entries.length > 0 && (
            <Badge ml={2} colorScheme="blue" fontSize="xs">
              {waitingEntries.length}{' '}
              {formatMessage(messages.waiting).toLowerCase()}
            </Badge>
          )}
        </Text>
      </Box>

      {entries.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={16}
          gap={2}
        >
          <Text color="gray.400" fontSize="md">
            {formatMessage(messages.noPatientsInQueue)}
          </Text>
        </Flex>
      ) : (
        <Box>
          {entries.map((entry, displayIndex) => {
            const isDone = entry.status === 'done'
            const waitingIdx = waitingEntries.findIndex(
              (e) => e.id === entry.id
            )
            const isFirstWaiting = waitingIdx === 0
            const isLastWaiting = waitingIdx === waitingEntries.length - 1
            const isBusy = actionInProgress === entry.id

            return (
              <Flex
                key={entry.id}
                data-testid={`queue-entry-${entry.id}`}
                px={6}
                py={4}
                align="center"
                gap={4}
                borderBottom="1px solid"
                borderColor="gray.100"
                _last={{ borderBottom: 'none' }}
                opacity={isDone ? 0.5 : 1}
                bg={isDone ? 'gray.50' : 'white'}
              >
                {/* Position badge */}
                <Flex
                  w={8}
                  h={8}
                  borderRadius="full"
                  bg={isDone ? 'gray.200' : 'blue.500'}
                  color="white"
                  align="center"
                  justify="center"
                  flexShrink={0}
                  fontSize="sm"
                  fontWeight="bold"
                >
                  {displayIndex + 1}
                </Flex>

                {/* Patient info */}
                <Box flex={1} minW={0}>
                  <Text
                    fontWeight="medium"
                    color={isDone ? 'gray.400' : 'gray.800'}
                    textDecoration={isDone ? 'line-through' : 'none'}
                    noOfLines={1}
                  >
                    {entry.patientName}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {intl.formatTime(entry.addedAt, {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </Text>
                  <Badge
                    mt={1}
                    colorScheme={
                      entry.source === 'appointment' ? 'purple' : 'gray'
                    }
                    fontSize="2xs"
                  >
                    {formatMessage(
                      entry.source === 'appointment'
                        ? messages.scheduledAppointment
                        : messages.walkIn
                    )}
                  </Badge>
                </Box>

                {/* Status badge */}
                <Badge
                  colorScheme={isDone ? 'gray' : 'green'}
                  fontSize="xs"
                  px={2}
                  py={0.5}
                  flexShrink={0}
                >
                  {isDone
                    ? formatMessage(messages.done)
                    : formatMessage(messages.waiting)}
                </Badge>

                {/* Action buttons — reorder only for waiting, remove for all */}
                <Flex gap={1} flexShrink={0}>
                  {!isDone && (
                    <>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="green"
                        isDisabled={isBusy}
                        onClick={async () => {
                          await handleDone(entry)
                        }}
                      >
                        {formatMessage(messages.markDone)}
                      </Button>
                      <IconButton
                        aria-label={formatMessage(messages.moveUp)}
                        icon={<ArrowUpIcon />}
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        isDisabled={isFirstWaiting || isBusy}
                        isLoading={isBusy}
                        onClick={async () => {
                          await handleMoveUp(entry)
                        }}
                      />
                      <IconButton
                        aria-label={formatMessage(messages.moveDown)}
                        icon={<ArrowDownIcon />}
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        isDisabled={isLastWaiting || isBusy}
                        isLoading={isBusy}
                        onClick={async () => {
                          await handleMoveDown(entry)
                        }}
                      />
                    </>
                  )}
                  <IconButton
                    aria-label={formatMessage(messages.removeFromQueue)}
                    icon={<CloseIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    isDisabled={isBusy}
                    isLoading={isBusy}
                    onClick={async () => {
                      await handleRemove(entry)
                    }}
                  />
                </Flex>
              </Flex>
            )
          })}
        </Box>
      )}
    </Box>
  )
}
